'use strict';
import {stateGoBack} from '../../../app/actions';
import {getOrganization, getOrgProfile, getUser} from '../../../app/model/reducers/state';
import {navStates} from '../../../app/router';
import {SCOPE_DESTROY} from '../../../config/constants/view-events';
import {LOADED, NO_DATA} from '../../../core-components/loading-icon/loading-states';
import {getId, getLink} from '../../../core-utilities/domain/ReduxUtils';
import {epicErrorHandler, eventToObservable} from '../../../core-utilities/rx-utils';
import {logger} from '../logger';
import inviteCreateTemplate from './invite-create.html';
import invitesImportTemplate from './invites-import.html';


module.exports = [
  '$scope',
  '$modal',
  '$popup',
  '$contextMenu',
  '$translate',
  '$miscUtils',
  '$ngRedux',
  '$permissions',
  '$toast',
  '$ionicViewSwitcher',
  'OrganizationDomain',
  'NotificationService',
  'INVITE_STATUS',
  'DEFAULT_ROLES',
  MembersManageController];

function MembersManageController(
  $scope,
  $modal,
  $popup,
  $contextMenu,
  $translate,
  $miscUtils,
  $ngRedux,
  $permissions,
  $toast,
  $ionicViewSwitcher,
  OrganizationDomain,
  NotificationService,
  INVITE_STATUS,
  DEFAULT_ROLES,
) {
  const vm = this;
  vm.INVITE_STATUS = INVITE_STATUS;
  vm.roles = DEFAULT_ROLES.toList();
  vm.roleLinks = DEFAULT_ROLES.toLinksList();
  loadRoleLabels(); // assigns vm.roleLabels;

  vm.openContext = $miscUtils.blockPromise(showMenu);

  vm.removeUser = $miscUtils.blockPromise(removeUser);
  vm.canEdit = canEdit;
  vm.promptUpdate = $miscUtils.blockPromise(promptUpdate);

  vm.openInviteImport = $miscUtils.blockPromise(openInviteImport);
  vm.create = $miscUtils.blockPromise(create);
  vm.edit = $miscUtils.blockPromise(edit);
  vm.resend = $miscUtils.blockPromise(resend);
  vm.cancel = $miscUtils.blockPromise(cancel);
  vm.deleteInvite = $miscUtils.blockPromise(deleteInvite);
  vm.goBack = goBack;

  const leave$ = eventToObservable($scope, SCOPE_DESTROY);
  $scope.$evalAsync(init);

  function init() {
    const hasPermission$ = $ngRedux.state$
      .map(getOrganization)
      .map(getId)
      .distinctUntilChanged()
      .switchMap(getPermission)
      .takeUntil(leave$)
      .catch(epicErrorHandler(logger, 'organization$'));

    hasPermission$
      .filter(hasPermission => !hasPermission)
      .subscribe(removeFromView);

    const organization$ = hasPermission$
      .filter(hasPermission => !!hasPermission)
      .switchMap(() =>
        $ngRedux.state$
          .map(getOrganization)
          .take(1)
      );

    organization$
      .takeUntil(leave$)
      .subscribe(organization => {
        $scope.$evalAsync(() => {
          vm.organization = organization;
        });
      });

    watch(organization$, org => OrganizationDomain.$getMembersRelations(getId(org)).$toObservable(), 'members', true);
    watch(organization$, org => OrganizationDomain.$getPersonnelList(getId(org)).$toObservable(), 'users', false);
    watch(organization$, org => OrganizationDomain.$getInvitesList(getId(org)).$toObservable(), 'invites', false);
  }

  function getPermission(orgId) {
    return $permissions
      .checkOrganizationPermission(orgId, 'organization_access_edit')
      .catch(err => false);
  }

  function removeFromView() {
    $translate('invites.manage.header.label').then(name =>
      $toast.showI18nMessage({
        messageId: 'view.permission.denied.message',
        messageParams: {viewName: name},
        type: $toast.TOAST_TYPES.warning,
      }));
    goBack();
  }

  /*****************************************************************************************************************
   * Header Buttons
   ****************************************************************************************************************/


  /*****************************************************************************************************************
   * Context Menu Actions
   ****************************************************************************************************************/

  function showMenu() {
    return $contextMenu.show({
      titleMessageId: 'organizations.members.context.title.label',
      cancelMessageId: 'defaults.context.cancel.label',
      actions: [
        {messageId: 'organizations.members.context.add.invite.label', action: edit.bind(null, null)},
        {messageId: 'organizations.members.context.csv.invite.label', action: openInviteImport},
      ],
    });
  }

  let openInviteImportCalled = false;

  function openInviteImport() {
    if (openInviteImportCalled) return;
    openInviteImportCalled = true;

    if (getOrganization($ngRedux.getState())) {
      return $modal.show({
        template: invitesImportTemplate,
        controller: 'InvitesImportController',
        animation: 'slide-in-up',
      }).then(__openInviteImportFinally, __openInviteImportFinally);
    } else {
      openInviteImportCalled = false;
      // TODO: shouldn't happen. recover??
      logger.debug('MembersManageController.openInviteImport: No organization detected. Aborting.');
    }
  }

  function __openInviteImportFinally(it) {
    openInviteImportCalled = false;
    return it;
  }


  /*****************************************************************************************************************
   * Controller Actions
   ****************************************************************************************************************/

  function goBack() {
    $ngRedux.dispatch(stateGoBack({state: navStates.orgsList}));
  }


  /*****************************************************************************************************************
   * User Actions
   ****************************************************************************************************************/

  // TODO: make a checkbox confirm to additionally select whether to delete the contact data
  function removeUser(profile) {
    const rootState = $ngRedux.getState();
    const organization = getOrganization(rootState);
    const orgProfile = getOrgProfile(rootState);
    if (profile && getLink(profile, 'aliasFor').id && getId(profile) === getId(orgProfile)) {
      // TODO: make popup warning user they cannot remove themselves right now
      // TODO: make sure this condition is correct
    } else if (profile) {
      return $popup
        .confirm({
          title: 'Remove User',
          /* eslint-disable-next-line max-len */
          template: '<div style="text-align: center;">Are you sure you want to remove <br/>' + profile.toString() + '?</div>',
        })
        .then(function (result) {
          if (result) {
            return organization.removeUser(getLink(profile, 'aliasFor').id, true); // delete alias for now
          }
        });
    } else {
      logger.warn('no profile selected to remove');
    }
  }

  function canEdit(userId) {
    const rootState = $ngRedux.getState();
    const user = getUser(rootState);
    const currentUserId = getId(user);
    if (!userId || !vm.members[currentUserId] || !vm.members[userId]) {
      return false;
    } else if (userId === currentUserId) {
      return false; // TODO: enable user's editing themselves, once appropriate checks are in place. Technically admin could edit themselves but owners cannot yet
    } else {
      const currentUserRole = vm.members[currentUserId].role.id;
      const targetUserRole = vm.members[userId].role.id;

      if (targetUserRole === DEFAULT_ROLES.OWNER) {
        return currentUserRole === DEFAULT_ROLES.OWNER;
      } else {
        return true;
      }
    }
  }

  function promptUpdate(profile, role, oldRole) {
    if (profile && role) {
      return Promise.props({
        title: $translate('organizations.roles.change.confirmation.title'),
        msg: $translate('organizations.roles.change.confirmation', {
          profile: profile.toString(),
          role: vm.roleLabels[role],
        }),
      })
        .then(messages =>
          /* TODO(i18n): convert to confirmI18n method when created */
          $popup.confirm({
            title: messages.title,
            template: messages.msg,
          }))
        .then(result => result
          ? getOrganization($ngRedux.getState())
            .changeRole(getLink(profile, 'aliasFor').id, role)
            .catch(notifyOfFailure)
          : null
        );
    }

    function notifyOfFailure(e) {
      // TODO: incorporate error?
      $translate('organizations.roles.change.error', {
        profile: profile.toString(),
        role: vm.roleLabels[role],
      }).then(msg => {
        NotificationService.reportError({
          removal: NotificationService.REMOVAL.NEVER,
          message: msg,
        });
      });

      vm.members[profile.aliasFor.$id].role = oldRole;
    }
  }


  /*****************************************************************************************************************
   * Invite Actions
   ****************************************************************************************************************/

  function create() {
    return edit(null);
  }

  function edit(invite) {
    return $modal.show({
      controller: 'InvitesCreateController',
      template: inviteCreateTemplate,
      locals: {
        person: null,
        invite: invite,
        organization: getOrganization($ngRedux.getState()),
      },
    });
  }

  function resend(invite) {
    // TODO: add a text input popup to get a message to send, showing the original message, for last minute editing.
    if (invite && !INVITE_STATUS.complete(invite.status)) {
      // return $popup.confirm({
      //    title: 'Delete Team Type',
      //    template: '<div style="text-align: center;">Are you sure you want to cancel the invite to <br/>' + invite.toString() + '?</div>'
      //  })
      //  .then(function (result) {
      //    if (result) {
      return invite.resend();
      //    }
      // });
    } else {
      logger.warn('no invite selected to cancel');
    }
  }

  // TODO(i18n): convert to message codes
  function cancel(invite) {
    if (invite) {
      return $popup
        .confirm({
          title: 'Cancel Invite',
          /* eslint-disable-next-line max-len */
          template: '<div style="text-align: center;">Are you sure you want to cancel the invite to <br/>' + invite.toString() + '?</div>',
        })
        .then(result => result ? invite.cancel() : null);
    } else {
      logger.warn('no invite selected to cancel');
    }
  }

  // TODO(i18n): convert to message codes
  function deleteInvite(invite) {
    if (invite) {
      return $popup
        .confirm({
          title: 'Delete Invite',
          /* eslint-disable-next-line max-len */
          template: '<div style="text-align: center;">Are you sure you want to delete the invite to <br/>' + invite.toString() + '?</div>',
        })
        .then(result => result ? invite.$delete() : null);
    } else {
      logger.warn('no invite selected to delete');
    }
  }


  /*****************************************************************************************************************
   * Presentation Logic
   ****************************************************************************************************************/


  /*****************************************************************************************************************
   * Load Data
   ****************************************************************************************************************/

  function loadRoleLabels() {
    vm.roleLabels = {};
    DEFAULT_ROLES.toList().map(role =>
      $translate(`organizations.roles.${role}.label`)
        .then(label => {
          $scope.$evalAsync(() => {
            vm.roleLabels[role] = label;
          });
        }));
  }

  function watch(organization$, getObservable, propName, setsLoaded) {
    organization$
      .switchMap(getObservable)
      .takeUntil(leave$)
      .subscribe(list => {
        $scope.$evalAsync(() => {
          vm[propName] = list;
          if (setsLoaded) {
            vm.loadingMode = list.$size() > 0 ? LOADED : NO_DATA;
          }
        });
      });
  }
}



// WEBPACK FOOTER //
// ./src/js/personnel/controllers/admin/MembersManageController.js